/**
 * SessionTimer.js - Displays countdown timer and expiry warnings
 * 
 * Shows:
 * - Countdown timer (HH:MM:SS) in top-left corner
 * - Warning popup at 3 minutes remaining
 * - Session expired overlay
 */

import React from 'react';
import { useSession } from '../context/SessionContext';

/**
 * Format seconds to HH:MM:SS display
 */
const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  const pad = (n) => String(n).padStart(2, '0');
  
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Get timer color based on remaining time
 */
const getTimerColor = (seconds) => {
  if (seconds <= 60) return '#f85149'; // Red - critical
  if (seconds <= 180) return '#d29922'; // Orange - warning
  return '#7ee787'; // Light green - ok
};

const SessionTimer = ({ onForceLogout, inline = false }) => {
  const {
    remainingSeconds,
    isTimerActive,
    showExpiryWarning,
    isSessionExpired,
    dismissExpiryWarning,
  } = useSession();
  
  // Don't render if timer is not active (no daily limit set)
  if (!isTimerActive && !isSessionExpired) {
    return null;
  }
  
  return (
    <>
      {/* Timer Display - fixed at top center of page, above all modals */}
      {isTimerActive && !isSessionExpired && (
        <div style={{
          position: 'fixed',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 99999, // Very high to show above all modals
          backgroundColor: 'rgba(13, 17, 23, 0.95)',
          border: `1px solid ${getTimerColor(remainingSeconds)}`,
          borderRadius: '8px',
          padding: '8px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        }}>
          <span style={{ 
            fontSize: '12px',
            color: '#f0e68c',
          }}>
            Session Time Left:
          </span>
          <span style={{
            fontFamily: 'monospace',
            fontSize: '16px',
            fontWeight: 'bold',
            color: getTimerColor(remainingSeconds),
            minWidth: '60px',
          }}>
            {formatTime(remainingSeconds)}
          </span>
        </div>
      )}
      
      {/* Warning Popup - 3 minutes remaining */}
      {showExpiryWarning && !isSessionExpired && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 10001,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <div style={{
            backgroundColor: '#161b22',
            borderRadius: '12px',
            border: '1px solid #d29922',
            padding: '24px 32px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(210, 153, 34, 0.3)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ 
              color: '#d29922', 
              margin: '0 0 12px 0',
              fontSize: '20px',
            }}>
              Session Expiring Soon!
            </h2>
            <p style={{ 
              color: '#c9d1d9', 
              margin: '0 0 20px 0',
              fontSize: '14px',
              lineHeight: '1.5',
            }}>
              Your daily session will expire in <strong style={{ color: '#d29922' }}>5 minutes</strong>.
              <br />
              Please save your work.
            </p>
            <button
              onClick={dismissExpiryWarning}
              style={{
                backgroundColor: '#d29922',
                color: '#0d1117',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e6a822'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#d29922'}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
      
      {/* Session Expired Overlay */}
      {isSessionExpired && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 10002,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <div style={{
            backgroundColor: '#161b22',
            borderRadius: '12px',
            border: '1px solid #f85149',
            padding: '32px 40px',
            maxWidth: '450px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(248, 81, 73, 0.3)',
          }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>⏰</div>
            <h2 style={{ 
              color: '#f85149', 
              margin: '0 0 12px 0',
              fontSize: '22px',
            }}>
              Daily Session Expired
            </h2>
            <p style={{ 
              color: '#c9d1d9', 
              margin: '0 0 16px 0',
              fontSize: '14px',
              lineHeight: '1.6',
            }}>
              You have reached your daily usage limit.
              <br />
              Please come back tomorrow to continue learning!
            </p>
            <p style={{ 
              color: '#8b949e', 
              margin: 0,
              fontSize: '13px',
            }}>
              Logging out...
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionTimer;
